import { EXPENSES, EXPENSE_ORDER } from '../../content/expenses';
import { fmtEur } from '../../engine/format';
import { t } from '../../engine/i18n';
import type { ExpenseId, GameState } from '../../engine/state';
import { update } from '../../engine/store';
import { applySaving, examine, savingValue, totalSaved } from '../../game/savingsHunt';
import { energyBlocker } from '../../game/work';
import { Card, Meter } from '../components';

export function SavingsTab({ state }: { state: GameState }) {
  const targets = EXPENSE_ORDER.filter((id) => state.expenses[id] && EXPENSES[id].hunt);
  const blocked = energyBlocker(state) !== null;
  return (
    <div class="grid">
      <Card title={t('savings.title')}>
        <p>{t('savings.intro')}</p>
        <p class="muted">{t('savings.total', { amount: fmtEur(totalSaved(state)) })}</p>
      </Card>
      {targets.map((id) => (
        <HuntCard key={id} id={id} state={state} blocked={blocked} />
      ))}
    </div>
  );
}

function HuntCard({ id, state, blocked }: { id: ExpenseId; state: GameState; blocked: boolean }) {
  const expense = state.expenses[id];
  const hunt = EXPENSES[id].hunt;
  if (!expense || !hunt) return null;
  const title = `${t(EXPENSES[id].label)} : ${t('common.perMonth', { amount: fmtEur(expense.amount) })}`;

  if (expense.optimized) {
    const saved = expense.saved ?? 0;
    return (
      <Card title={title}>
        {saved > 0 ? (
          <>
            <p class="tone-good">
              {t('savings.optimized', { after: fmtEur(expense.amount), before: fmtEur(expense.amount + saved) })}
            </p>
            <p class="muted">{t('savings.value', { monthly: fmtEur(saved), fv: fmtEur(savingValue(saved)) })}</p>
          </>
        ) : (
          <p class="muted">{t('savings.nothing')}</p>
        )}
      </Card>
    );
  }

  if (expense.examined < hunt.clicks) {
    return (
      <Card title={title}>
        <Meter value={expense.examined} max={hunt.clicks} label={t(EXPENSES[id].label)} />
        <button disabled={blocked} onClick={() => update((s) => examine(s, id))}>
          {t('savings.examine', { done: expense.examined, total: hunt.clicks })}
        </button>
      </Card>
    );
  }

  const options = hunt.options.filter((option) => !option.available || option.available(state));
  return (
    <Card title={title}>
      <p>{t('savings.found')}</p>
      {options.length === 0 && <p class="muted">{t('savings.nothing')}</p>}
      <div class="choices">
        {options.map((option) => (
          <button key={option.id} onClick={() => update((s) => applySaving(s, id, option.id))}>
            {t(option.label, option.params?.(state))}
          </button>
        ))}
      </div>
    </Card>
  );
}
